import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import { Copy, GripVertical, Lock, Plus, Trash2 } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useState } from "react";
import type React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import type { EmailBlock, EmailTemplate } from "../types";
import { MarketingMailboxCard } from "./MarketingMailboxCard";

/**
 * Email template builder - block-based, drag to reorder, brand accent color,
 * exports email-safe table-based HTML.
 *
 * CAN-SPAM is enforced structurally, not by policy: every template carries a
 * locked compliance footer with the sender's physical mailing address and an
 * unsubscribe link ({{unsubscribe_url}} merge tag). The footer cannot be
 * removed, and HTML export stays disabled until a mailing address is set.
 */

let uid = Date.now() % 100000;
const nextUid = () => String(++uid);

const BLOCK_TYPES: { type: EmailBlock["type"]; name: string }[] = [
  { type: "heading", name: "Heading" },
  { type: "text", name: "Text" },
  { type: "button", name: "Button" },
  { type: "image", name: "Image" },
  { type: "divider", name: "Divider" },
  { type: "spacer", name: "Spacer" },
  { type: "logo", name: "Logo" },
  { type: "bullets", name: "Bullet list" },
];

const defaultBlock = (type: EmailBlock["type"]): EmailBlock => ({
  id: nextUid(),
  type,
  text:
    type === "heading"
      ? "Headline that leads with their problem"
      : type === "text"
        ? "Write like a person. One idea per paragraph."
        : type === "button"
          ? "Book a call"
          : type === "bullets"
            ? "First point\nSecond point\nThird point"
            : "",
  url: type === "button" ? "https://" : type === "image" ? "https://" : undefined,
  align: "left",
});

const FONT_STACKS: Record<string, string> = {
  arial: "Arial,Helvetica,sans-serif",
  georgia: "Georgia,'Times New Roman',serif",
  verdana: "Verdana,Geneva,sans-serif",
  trebuchet: "'Trebuchet MS',Helvetica,sans-serif",
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const generateEmailHtml = (
  template: EmailTemplate,
  mailingAddress: string,
) => {
  const font = FONT_STACKS[template.fontFamily ?? "arial"] ?? FONT_STACKS.arial;
  const bg = template.backgroundColor ?? "#f1f4f7";
  const card = template.cardColor ?? "#ffffff";
  const blockHtml = template.blocks
    .map((b) => {
      const align = b.align ?? "left";
      const headingSize = b.size === "s" ? 19 : b.size === "l" ? 30 : 24;
      const textSize = b.size === "s" ? 13 : b.size === "l" ? 17 : 15;
      switch (b.type) {
        case "heading":
          return `      <tr><td style="padding:24px 32px 8px;font-size:${headingSize}px;font-weight:600;color:${b.color ?? "#1a2332"};font-family:${font};text-align:${align};">${escapeHtml(b.text ?? "")}</td></tr>`;
        case "text":
          return `      <tr><td style="padding:8px 32px;font-size:${textSize}px;line-height:1.6;color:${b.color ?? "#33404f"};font-family:${font};text-align:${align};">${escapeHtml(b.text ?? "").replace(/\n/g, "<br />")}</td></tr>`;
        case "button":
          return `      <tr><td style="padding:20px 32px;text-align:${align};"><a href="${b.url ?? "#"}" style="display:inline-block;padding:12px 24px;background:${template.accentColor};color:#ffffff;text-decoration:none;border-radius:4px;font-size:15px;font-family:${font};">${escapeHtml(b.text ?? "")}</a></td></tr>`;
        case "image": {
          const w = b.width === "half" ? 268 : 536;
          return `      <tr><td style="padding:16px 32px;text-align:${align};"><img src="${b.url ?? ""}" alt="${escapeHtml(b.alt ?? "")}" width="${w}" style="max-width:100%;height:auto;display:inline-block;border-radius:4px;" /></td></tr>`;
        }
        case "divider":
          return `      <tr><td style="padding:16px 32px;"><hr style="border:none;border-top:1px solid #e2e8f0;margin:0;" /></td></tr>`;
        case "bullets": {
          const items = (b.text ?? "")
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => `<li style="margin:4px 0;">${escapeHtml(l)}</li>`)
            .join("");
          return `      <tr><td style="padding:8px 32px;font-size:${textSize}px;line-height:1.6;color:${b.color ?? "#33404f"};font-family:${font};text-align:${align};"><ul style="margin:0;padding-left:20px;">${items}</ul></td></tr>`;
        }
        case "spacer":
          return `      <tr><td style="height:24px;line-height:24px;font-size:0;">&nbsp;</td></tr>`;
        case "logo":
          return `      <tr><td style="padding:28px 32px 4px;text-align:${align};font-size:18px;font-weight:700;letter-spacing:0.02em;color:#1a2332;font-family:${font};">{{company_name}}</td></tr>`;
      }
    })
    .join("\n");

  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /><meta name="viewport" content="width=device-width" /><title>${escapeHtml(template.subject)}</title></head>
<body style="margin:0;padding:0;background:${bg};">
  <div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">${escapeHtml(template.preheader ?? "")}</div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${bg};padding:24px 0;">
    <tr><td align="center">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background:${card};border-radius:6px;max-width:600px;width:100%;">
${blockHtml}
      <!-- CAN-SPAM compliance footer: physical address + unsubscribe. Required; do not remove. -->
      <tr><td style="padding:32px;border-top:1px solid #e2e8f0;font-size:12px;line-height:1.6;color:#8593a3;font-family:${font};">
        ${escapeHtml(mailingAddress).replace(/\n/g, "<br />")}<br />
        You received this email because of your relationship with us.
        <a href="{{unsubscribe_url}}" style="color:#8593a3;">Unsubscribe</a>
      </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
};

export const EmailBuilder = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const templates = config.emailTemplates ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(
    templates[0]?.id ?? null,
  );
  const [copied, setCopied] = useState(false);

  const template = templates.find((t) => t.id === selectedId) ?? null;
  const addressSet = (config.mailingAddress ?? "").trim().length > 0;
  const senderSet = (config.marketingFromEmail ?? "").trim().length > 0;

  // The domain half of the sending address is never a free choice - only
  // one domain is ever approved at a time (HQ-set, decision #79) - so the
  // UI can only offer that one option instead of letting someone type in
  // an arbitrary domain that will just silently fail to send. Composing
  // local@approvedDomain here also means a domain HQ re-approves later
  // takes effect on the next edit with no migration needed.
  const approvedDomain = (config.marketingApprovedDomain ?? "").trim();
  const currentFromEmail = config.marketingFromEmail ?? "";
  const atIndex = currentFromEmail.lastIndexOf("@");
  const senderLocalPart = atIndex === -1 ? currentFromEmail : currentFromEmail.slice(0, atIndex);
  const updateSenderLocalPart = (nextLocal: string) =>
    updateConfig({
      ...config,
      marketingFromEmail: nextLocal && approvedDomain ? `${nextLocal}@${approvedDomain}` : "",
    });

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row fires immediately for discrete actions (add/remove a
  // block, toggle align/size/width, create/delete a template) and on blur
  // for free-typed fields (template name/subject/preheader, block text/
  // url/alt, colors, the mailing address), so keystrokes don't spam the
  // network. Without this, templates only ever lived in the browser's
  // local store - never actually reaching the database.
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify("Couldn't save that - you may need admin access to update email templates.", {
        type: "error",
      });
    }
  };
  const saveTemplates = (next: EmailTemplate[], opts: { persistNow?: boolean } = {}) => {
    const nextConfig = { ...config, emailTemplates: next };
    updateConfig(nextConfig);
    if (opts.persistNow !== false) void persist(nextConfig);
  };
  const flushTemplates = () => void persist({ ...config, emailTemplates: templates });
  const patchTemplate = (patch: Partial<EmailTemplate>, opts?: { persistNow?: boolean }) => {
    if (!template) return;
    saveTemplates(
      templates.map((t) => (t.id === template.id ? { ...t, ...patch } : t)),
      opts,
    );
  };

  const createTemplate = () => {
    const id = `tpl-${nextUid()}`;
    saveTemplates([
      ...templates,
      {
        id,
        name: "New template",
        subject: "Subject line",
        accentColor: "#334155",
        blocks: [defaultBlock("heading"), defaultBlock("text"), defaultBlock("button")],
      },
    ]);
    setSelectedId(id);
  };

  const onDragEnd: OnDragEndResponder = ({ source, destination }) => {
    if (!template || !destination) return;
    const blocks = [...template.blocks];
    const [moved] = blocks.splice(source.index, 1);
    blocks.splice(destination.index, 0, moved!);
    patchTemplate({ blocks });
  };

  const html = template
    ? generateEmailHtml(template, config.mailingAddress || "")
    : "";

  return (
    <div className="flex flex-col gap-4">
      {/* CAN-SPAM: sender address */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-1">
            Physical mailing address{" "}
            <span className="text-muted-foreground font-normal">
              - required on every email (CAN-SPAM)
            </span>
          </p>
          <Textarea
            rows={2}
            placeholder={'e.g. "Grey Matter, 123 Main St Suite 400, Cincinnati, OH 45202"'}
            value={config.mailingAddress ?? ""}
            onChange={(e) => updateConfig({ ...config, mailingAddress: e.target.value })}
            onBlur={() => void persist(config)}
          />
          {!addressSet && (
            <p className="text-xs text-destructive mt-1">
              HTML export is disabled until a mailing address is set - every
              marketing email must carry one.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Sending address: sequence/workflow email always sends from this
          workspace's OWN domain, never Grey Matter's shared system address.
          Only the mailbox name is a free choice - the domain is fixed to
          whatever's approved, never typed in, so there's nothing here that
          can silently fail to send from a mismatched domain. */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-1">
            Marketing sending address{" "}
            <span className="text-muted-foreground font-normal">
              - where Sequences and Workflows email sends from
            </span>
          </p>
          <div className="flex items-center gap-1">
            <Input
              placeholder="hello"
              value={senderLocalPart}
              disabled={!approvedDomain}
              onChange={(e) => updateSenderLocalPart(e.target.value)}
              onBlur={() => void persist(config)}
              className="flex-1"
            />
            <span className="text-muted-foreground">@</span>
            <select
              aria-label="Sending domain"
              className="h-9 rounded border border-border bg-transparent px-2 text-sm disabled:opacity-50"
              value={approvedDomain}
              disabled={!approvedDomain}
              onChange={() => {
                /* only one option is ever offered - nothing to select */
              }}
            >
              {approvedDomain ? (
                <option value={approvedDomain}>{approvedDomain}</option>
              ) : (
                <option value="">none approved yet</option>
              )}
            </select>
          </div>
          <p className="text-sm font-medium mt-3 mb-1">
            Sender name{" "}
            <span className="text-muted-foreground font-normal">
              - who the email shows as from, e.g. "Acme Co" or "Jane at Acme Co"
            </span>
          </p>
          <Input
            aria-label="Sender name"
            placeholder="e.g. Acme Co"
            maxLength={100}
            value={config.marketingFromName ?? ""}
            onChange={(e) => updateConfig({ ...config, marketingFromName: e.target.value })}
            onBlur={() => void persist(config)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {approvedDomain
              ? senderSet
                ? "Sending stops if this domain isn't (or stops being) verified on our end - ask your Grey Matter contact to confirm."
                : "Sequence and workflow email sends are OFF until you pick a mailbox name above."
              : "No domain is approved for this workspace yet, so sending stays off - ask your Grey Matter contact to verify one and approve it."}
          </p>
          <label className="flex items-start gap-2 mt-3">
            <Switch
              aria-label="Track opens and clicks"
              checked={config.marketingTrackEngagement !== false}
              onCheckedChange={(checked) => {
                const next = { ...config, marketingTrackEngagement: checked };
                updateConfig(next);
                void persist(next);
              }}
            />
            <span className="text-sm">
              Track opens and clicks
              <span className="block text-xs text-muted-foreground">
                Shown on each contact's emails, and counted by lead scoring. Turn off
                if this workspace mustn't use tracking pixels or rewritten links.
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      <MarketingMailboxCard />

      <div className="flex flex-wrap items-center gap-2">
        {templates.map((t) => (
          <Button
            key={t.id}
            variant={t.id === selectedId ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedId(t.id)}
          >
            {t.name}
          </Button>
        ))}
        <Button variant="outline" size="sm" onClick={createTemplate}>
          <Plus className="w-4 h-4" /> New template
        </Button>
      </div>

      {template && (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Builder */}
          <Card>
            <CardContent className="p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Input
                  aria-label="Template name"
                  value={template.name}
                  onChange={(e) => patchTemplate({ name: e.target.value }, { persistNow: false })}
                  onBlur={flushTemplates}
                  className="font-medium"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete template"
                  onClick={() => {
                    saveTemplates(templates.filter((t) => t.id !== template.id));
                    setSelectedId(templates.find((t) => t.id !== template.id)?.id ?? null);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <div className="flex flex-col gap-2">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Subject</p>
                    <Input
                      value={template.subject}
                      onChange={(e) =>
                        patchTemplate({ subject: e.target.value }, { persistNow: false })
                      }
                      onBlur={flushTemplates}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Preview text (shows after the subject in the inbox)
                    </p>
                    <Input
                      value={template.preheader ?? ""}
                      onChange={(e) =>
                        patchTemplate({ preheader: e.target.value }, { persistNow: false })
                      }
                      onBlur={flushTemplates}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Font</p>
                    <select
                      aria-label="Font family"
                      className="h-9 rounded border border-border bg-transparent px-2 text-sm"
                      value={template.fontFamily ?? "arial"}
                      onChange={(e) =>
                        patchTemplate({ fontFamily: e.target.value as EmailTemplate["fontFamily"] })
                      }
                    >
                      <option value="arial">Arial</option>
                      <option value="georgia">Georgia</option>
                      <option value="verdana">Verdana</option>
                      <option value="trebuchet">Trebuchet</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Page</p>
                      <input
                        type="color"
                        aria-label="Background color"
                        className="h-9 w-10 rounded border border-border bg-transparent"
                        value={template.backgroundColor ?? "#f1f4f7"}
                        onChange={(e) =>
                          patchTemplate({ backgroundColor: e.target.value }, { persistNow: false })
                        }
                        onBlur={flushTemplates}
                      />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Card</p>
                      <input
                        type="color"
                        aria-label="Card color"
                        className="h-9 w-10 rounded border border-border bg-transparent"
                        value={template.cardColor ?? "#ffffff"}
                        onChange={(e) =>
                          patchTemplate({ cardColor: e.target.value }, { persistNow: false })
                        }
                        onBlur={flushTemplates}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">Accent</p>
                  <input
                    type="color"
                    aria-label="Accent color"
                    className="h-9 w-12 rounded border border-border bg-transparent"
                    value={template.accentColor}
                    onChange={(e) =>
                      patchTemplate({ accentColor: e.target.value }, { persistNow: false })
                    }
                    onBlur={flushTemplates}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                Images: paste a URL or upload below - uploads are hosted
                automatically per tenant at deployment (email clients require
                hosted images; embedded ones get stripped by Gmail).
              </p>

              <p className="text-xs text-muted-foreground">
                Personalization: use {"{{first_name}}"}, {"{{company}}"},{" "}
                {"{{owner_name}}"}, or {"{{booking_url}}"} in any text - filled
                per recipient at send time.
              </p>
              <div className="flex flex-wrap gap-1">
                {BLOCK_TYPES.map((b) => (
                  <Button
                    key={b.type}
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      patchTemplate({ blocks: [...template.blocks, defaultBlock(b.type)] })
                    }
                  >
                    <Plus className="w-3.5 h-3.5" /> {b.name}
                  </Button>
                ))}
              </div>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="email-blocks">
                  {(dropProvided) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className="flex flex-col gap-2"
                    >
                      {template.blocks.map((b, index) => (
                        <Draggable key={b.id} draggableId={b.id} index={index}>
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={dragProvided.draggableProps.style as React.CSSProperties}
                              className="flex items-start gap-2 border border-border rounded p-2 bg-card"
                            >
                              <span {...dragProvided.dragHandleProps} className="pt-2">
                                <GripVertical className="w-4 h-4 text-muted-foreground" />
                              </span>
                              <div className="flex-1 flex flex-col gap-1">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  {b.type}
                                </p>
                                {(b.type === "heading" || b.type === "button") && (
                                  <Input
                                    value={b.text ?? ""}
                                    onChange={(e) =>
                                      patchTemplate(
                                        {
                                          blocks: template.blocks.map((x) =>
                                            x.id === b.id ? { ...x, text: e.target.value } : x,
                                          ),
                                        },
                                        { persistNow: false },
                                      )
                                    }
                                    onBlur={flushTemplates}
                                  />
                                )}
                                {(b.type === "text" || b.type === "bullets") && (
                                  <Textarea
                                    rows={3}
                                    value={b.text ?? ""}
                                    onChange={(e) =>
                                      patchTemplate(
                                        {
                                          blocks: template.blocks.map((x) =>
                                            x.id === b.id ? { ...x, text: e.target.value } : x,
                                          ),
                                        },
                                        { persistNow: false },
                                      )
                                    }
                                    onBlur={flushTemplates}
                                  />
                                )}
                                {(b.type === "heading" || b.type === "text" || b.type === "button" || b.type === "logo" || b.type === "bullets") && (
                                  <div className="flex items-center gap-3">
                                    <button
                                      type="button"
                                      className="text-xs text-muted-foreground underline"
                                      onClick={() =>
                                        patchTemplate({
                                          blocks: template.blocks.map((x) =>
                                            x.id === b.id
                                              ? {
                                                  ...x,
                                                  align:
                                                    x.align === "left" || !x.align
                                                      ? "center"
                                                      : x.align === "center"
                                                        ? "right"
                                                        : "left",
                                                }
                                              : x,
                                          ),
                                        })
                                      }
                                    >
                                      align: {b.align ?? "left"}
                                    </button>
                                    {(b.type === "heading" || b.type === "text" || b.type === "bullets") && (
                                      <>
                                        <button
                                          type="button"
                                          className="text-xs text-muted-foreground underline"
                                          onClick={() =>
                                            patchTemplate({
                                              blocks: template.blocks.map((x) =>
                                                x.id === b.id
                                                  ? {
                                                      ...x,
                                                      size:
                                                        x.size === "s" ? "m" : x.size === "l" ? "s" : "l",
                                                    }
                                                  : x,
                                              ),
                                            })
                                          }
                                        >
                                          size: {b.size ?? "m"}
                                        </button>
                                        <label className="flex items-center gap-1 text-xs text-muted-foreground">
                                          color
                                          <input
                                            type="color"
                                            aria-label="Text color"
                                            className="h-5 w-7 rounded border border-border bg-transparent"
                                            value={b.color ?? "#33404f"}
                                            onChange={(e) =>
                                              patchTemplate(
                                                {
                                                  blocks: template.blocks.map((x) =>
                                                    x.id === b.id ? { ...x, color: e.target.value } : x,
                                                  ),
                                                },
                                                { persistNow: false },
                                              )
                                            }
                                            onBlur={flushTemplates}
                                          />
                                        </label>
                                      </>
                                    )}
                                  </div>
                                )}
                                {b.type === "image" && (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      id={`img-upload-${b.id}`}
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const reader = new FileReader();
                                        reader.onload = () =>
                                          patchTemplate({
                                            blocks: template.blocks.map((x) =>
                                              x.id === b.id
                                                ? { ...x, url: String(reader.result) }
                                                : x,
                                            ),
                                          });
                                        reader.readAsDataURL(file);
                                        e.target.value = "";
                                      }}
                                    />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        document.getElementById(`img-upload-${b.id}`)?.click()
                                      }
                                    >
                                      Upload image
                                    </Button>
                                    <Input
                                      placeholder="Alt text (what the image shows)"
                                      value={b.alt ?? ""}
                                      onChange={(e) =>
                                        patchTemplate(
                                          {
                                            blocks: template.blocks.map((x) =>
                                              x.id === b.id ? { ...x, alt: e.target.value } : x,
                                            ),
                                          },
                                          { persistNow: false },
                                        )
                                      }
                                      onBlur={flushTemplates}
                                    />
                                    <button
                                      type="button"
                                      className="text-xs text-muted-foreground underline whitespace-nowrap"
                                      onClick={() =>
                                        patchTemplate({
                                          blocks: template.blocks.map((x) =>
                                            x.id === b.id
                                              ? { ...x, width: x.width === "half" ? "full" : "half" }
                                              : x,
                                          ),
                                        })
                                      }
                                    >
                                      {b.width === "half" ? "half width" : "full width"}
                                    </button>
                                  </div>
                                )}
                                {(b.type === "button" || b.type === "image") && (
                                  <Input
                                    placeholder={b.type === "button" ? "Link URL" : "Image URL (or upload above)"}
                                    value={b.url ?? ""}
                                    onChange={(e) =>
                                      patchTemplate(
                                        {
                                          blocks: template.blocks.map((x) =>
                                            x.id === b.id ? { ...x, url: e.target.value } : x,
                                          ),
                                        },
                                        { persistNow: false },
                                      )
                                    }
                                    onBlur={flushTemplates}
                                  />
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Remove block"
                                onClick={() =>
                                  patchTemplate({
                                    blocks: template.blocks.filter((x) => x.id !== b.id),
                                  })
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Locked compliance footer */}
              <div className="flex items-start gap-2 border border-dashed border-border rounded p-2 bg-muted/50">
                <Lock className="w-4 h-4 text-muted-foreground mt-1" />
                <div className="text-xs text-muted-foreground">
                  <p className="font-medium">Compliance footer (always included)</p>
                  <p>
                    {addressSet
                      ? config.mailingAddress
                      : "Your physical mailing address - set it above"}
                    {" · "}Unsubscribe link
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview + export */}
          <div className="flex flex-col gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
                  Preview - subject: {template.subject}
                </p>
                <div
                  className="border border-border rounded overflow-hidden text-[#33404f]"
                  style={{ background: template.backgroundColor ?? "#f1f4f7", padding: 16 }}
                >
                  <div
                    className="p-6 flex flex-col gap-3 rounded"
                    style={{
                      background: template.cardColor ?? "#ffffff",
                      fontFamily: FONT_STACKS[template.fontFamily ?? "arial"],
                    }}
                  >
                    {template.blocks.map((b) => {
                      switch (b.type) {
                        case "heading":
                          return (
                            <p
                              key={b.id}
                              className="font-semibold"
                              style={{
                                textAlign: b.align ?? "left",
                                color: b.color ?? "#1a2332",
                                fontSize: b.size === "s" ? 16 : b.size === "l" ? 26 : 20,
                              }}
                            >
                              {b.text}
                            </p>
                          );
                        case "text":
                          return (
                            <p
                              key={b.id}
                              className="whitespace-pre-line"
                              style={{
                                textAlign: b.align ?? "left",
                                color: b.color ?? "#33404f",
                                fontSize: b.size === "s" ? 12 : b.size === "l" ? 16 : 14,
                              }}
                            >
                              {b.text}
                            </p>
                          );
                        case "bullets":
                          return (
                            <ul
                              key={b.id}
                              className="list-disc pl-5"
                              style={{
                                textAlign: b.align ?? "left",
                                color: b.color ?? "#33404f",
                                fontSize: b.size === "s" ? 12 : b.size === "l" ? 16 : 14,
                              }}
                            >
                              {(b.text ?? "").split("\n").filter(Boolean).map((l, i) => (
                                <li key={i}>{l}</li>
                              ))}
                            </ul>
                          );
                        case "button":
                          return (
                            <span
                              key={b.id}
                              className="inline-block w-fit px-4 py-2 rounded text-white text-sm"
                              style={{ background: template.accentColor }}
                            >
                              {b.text}
                            </span>
                          );
                        case "image":
                          return b.url && b.url !== "https://" ? (
                            <img
                              key={b.id}
                              src={b.url}
                              alt={b.alt ?? ""}
                              className="rounded"
                              style={{
                                width: b.width === "half" ? "50%" : "100%",
                                alignSelf: b.align === "center" ? "center" : "flex-start",
                              }}
                            />
                          ) : (
                            <div
                              key={b.id}
                              className="h-24 bg-[#f1f4f7] rounded flex items-center justify-center text-xs text-[#8593a3]"
                            >
                              image - paste a URL or upload
                            </div>
                          );
                        case "divider":
                          return <hr key={b.id} className="border-[#e2e8f0]" />;
                        case "spacer":
                          return <div key={b.id} className="h-6" />;
                        case "logo":
                          return (
                            <p
                              key={b.id}
                              className="text-lg font-bold tracking-wide text-[#1a2332]"
                              style={{ textAlign: b.align === "center" ? "center" : "left" }}
                            >
                              {"{{company_name}}"}
                            </p>
                          );
                      }
                    })}
                    <div className="border-t border-[#e2e8f0] pt-3 text-[11px] text-[#8593a3]">
                      {addressSet ? config.mailingAddress : "[mailing address required]"} ·{" "}
                      <span className="underline">Unsubscribe</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Email HTML
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!addressSet}
                    title={addressSet ? undefined : "Set your mailing address first"}
                    onClick={() => {
                      navigator.clipboard?.writeText(html);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                  >
                    <Copy className="w-4 h-4" /> {copied ? "Copied" : "Copy HTML"}
                  </Button>
                </div>
                <Textarea
                  readOnly
                  value={addressSet ? html : "Set your physical mailing address above to unlock export."}
                  rows={10}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Table-based, inline-styled HTML that renders in every email
                  client. The {"{{unsubscribe_url}}"} merge tag is filled by
                  the sending platform when campaigns go out.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      {!template && (
        <p className="text-sm text-muted-foreground">
          No templates yet - create one to start designing.
        </p>
      )}
    </div>
  );
};
